import { useMemo } from "react";

import {
  type InfiniteData,
  keepPreviousData,
  type QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query";
import axios from "axios";

import { attachAuthInterceptors } from "~/v2/api/auth/authFailure";
import { FeatureFlags, useFeatureEnabled } from "~/v2/helpers/featureFlags";
import { getTokenizedEquityWalletApiUrl } from "~/v2/helpers/tokenizedEquityTrading";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { parseCryptoAssetSymbol } from "~/v2/utils/cryptoAssets";
import { queryKeys } from "../queryKeys";
import {
  type CryptoAddress,
  type CryptoAsset,
  type CryptoFees,
  type CryptoTransfer,
  type CryptoWhitelistedAddress,
  type GetCryptoFeesParams,
  getCryptoAddress,
  getCryptoFees,
  getCryptoTransfers,
  getWhitelistedAddresses,
  getWhitelistedAddressesForSymbol,
  listCryptoAssets,
  searchVasps,
  type TravelRuleInfo,
  updateTravelRuleInfo,
  type VaspSearchResponse,
} from "../rest/cryptoWallet";
import { useMutationRequest } from "./useMutationRequest";
import useRequest, { type QueryOptions } from "./useRequest";

export const useCryptoWalletAPI = () => {
  const { currentAccount } = useAccountContext();
  const cryptoBroker = currentAccount?.crypto_broker;
  const isTokenizedEquityTrading = useFeatureEnabled(
    FeatureFlags.TOKENIZED_EQUITY_TRADING,
  );

  let baseUrl = window.env.CRYPTO_WALLET_API_URL;

  if (isTokenizedEquityTrading) {
    baseUrl = getTokenizedEquityWalletApiUrl();
  } else if (cryptoBroker === "BS") {
    // cryptowallet-alpacax
    baseUrl = window.env.CRYPTO_WALLETX_API_URL;
  }

  baseUrl = (baseUrl as string)?.replace(/\/v\d+/, "");

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${baseUrl}/v2`,
    });

    return attachAuthInterceptors(instance);
  }, [baseUrl]);
  return api;
};

export interface UseGetCryptoAssetsArgs {
  accountId: string;
  options?: QueryOptions<CryptoAsset[]>;
}

export const useGetCryptoAssets = ({
  accountId,
  options,
}: UseGetCryptoAssetsArgs) => {
  const instance = useCryptoWalletAPI();
  return useRequest(
    queryKeys.crypto.assets(accountId),
    async () => listCryptoAssets(instance, accountId),
    {
      placeholderData: [],
      ...options,
      enabled: !!accountId && (options?.enabled ?? true),
    },
  );
};

export interface UseGetCryptoAddressArgs {
  accountId: string;
  symbol: string;
  options?: QueryOptions<CryptoAddress>;
}

export const useGetCryptoAddress = ({
  accountId,
  symbol,
  options,
}: UseGetCryptoAddressArgs) => {
  const { chain, asset } = parseCryptoAssetSymbol(symbol);
  const instance = useCryptoWalletAPI();
  return useRequest(
    queryKeys.crypto.address(accountId, chain, asset),
    () => getCryptoAddress(instance, accountId, symbol),
    {
      ...options,
      enabled: !!accountId && !!symbol && (options?.enabled ?? true),
    },
  );
};

export interface UseGetCryptoTransfersArgs {
  accountId: string;
  options?: QueryOptions<CryptoTransfer[]>;
}

export const useGetCryptoTransfers = ({
  accountId,
  options,
}: UseGetCryptoTransfersArgs) => {
  const instance = useCryptoWalletAPI();
  return useRequest(
    queryKeys.crypto.transfers(accountId),
    () => getCryptoTransfers(instance, accountId),
    {
      ...options,
      enabled: !!accountId && (options?.enabled ?? true),
    },
  );
};

export interface UseGetCryptoWhitelistsArgs {
  accountId: string;
  isApproved?: boolean;
  options?: QueryOptions<CryptoWhitelistedAddress[]>;
}

export const useGetCryptoWhitelists = ({
  accountId,
  isApproved,
  options,
}: UseGetCryptoWhitelistsArgs) => {
  const instance = useCryptoWalletAPI();
  return useRequest(
    queryKeys.crypto.whitelists(accountId, isApproved),
    () => getWhitelistedAddresses(instance, accountId, isApproved),
    {
      placeholderData: [],
      ...options,
      enabled: !!accountId && (options?.enabled ?? true),
    },
  );
};

export interface UseGetCryptoApprovedWhitelistsBySymbolArgs {
  accountId: string;
  symbol: string;
  options?: QueryOptions<CryptoWhitelistedAddress[]>;
}

export const useGetCryptoApprovedWhitelistsBySymbol = ({
  accountId,
  symbol,
  options,
}: UseGetCryptoApprovedWhitelistsBySymbolArgs) => {
  const instance = useCryptoWalletAPI();
  return useRequest(
    queryKeys.crypto.approvedWhitelistsBySymbol(accountId, symbol),
    () => getWhitelistedAddressesForSymbol(instance, accountId, symbol, true),
    {
      placeholderData: [],
      ...options,
      enabled: !!accountId && !!symbol && (options?.enabled ?? true),
    },
  );
};

export interface UseGetCryptoNetworkFeeArgs {
  accountId: string;
  params: GetCryptoFeesParams;
  options?: QueryOptions<CryptoFees>;
}

export const useGetCryptoNetworkFee = ({
  accountId,
  params,
  options,
}: UseGetCryptoNetworkFeeArgs) => {
  const instance = useCryptoWalletAPI();
  return useRequest(
    queryKeys.crypto.networkFee(
      accountId,
      params.chain,
      params.asset,
      params.from_address,
      params.to_address,
      params.amount,
    ),
    () => getCryptoFees(instance, accountId, params),
    {
      ...options,
      enabled: !!accountId && (options?.enabled ?? true),
    },
  );
};

export const MIN_VASP_QUERY_LENGTH = 2;

export interface UseSearchVaspsArgs {
  accountId: string;
  query: string;
  page?: number;
  perPage?: number;
  options?: QueryOptions<VaspSearchResponse>;
}

export const useSearchVasps = ({
  accountId,
  query,
  page = 0,
  perPage = 20,
  options,
}: UseSearchVaspsArgs) => {
  const instance = useCryptoWalletAPI();
  return useRequest(
    queryKeys.crypto.vasps(accountId, query, page, perPage),
    () => searchVasps(instance, accountId, { query, page, perPage }),
    {
      ...options,
      enabled:
        !!accountId &&
        query.length >= MIN_VASP_QUERY_LENGTH &&
        (options?.enabled ?? true),
    },
  );
};

export interface UseSearchVaspsInfiniteArgs {
  accountId: string;
  query: string;
  perPage?: number;
  enabled?: boolean;
}

/**
 * Notabene's directory is zero-indexed, so the Nth page fetched is page N - 1
 * and the next page number is simply the count of pages already held.
 *
 * `pages` is authoritative when present, and survives Notabene capping
 * `per_page` below what we asked for. It is dropped by cryptowallet's
 * `omitempty` only when zero, i.e. when there is nothing to paginate, so a
 * short final page is the fallback signal.
 */
export const getNextVaspPageParam = (
  lastPage: VaspSearchResponse,
  allPages: VaspSearchResponse[],
  perPage: number,
): number | undefined => {
  if (lastPage.pages) {
    return allPages.length >= lastPage.pages ? undefined : allPages.length;
  }
  return (lastPage.vasps?.length ?? 0) < perPage ? undefined : allPages.length;
};

/**
 * Paginated VASP search. `query` is expected to already be debounced by the
 * caller.
 */
export const useSearchVaspsInfinite = ({
  accountId,
  query,
  perPage = 20,
  enabled = true,
}: UseSearchVaspsInfiniteArgs) => {
  const instance = useCryptoWalletAPI();

  return useInfiniteQuery<
    VaspSearchResponse,
    Error,
    InfiniteData<VaspSearchResponse>,
    QueryKey,
    number
  >({
    queryKey: queryKeys.crypto.vaspsInfinite(accountId, query, perPage),
    queryFn: ({ pageParam }) =>
      searchVasps(instance, accountId, { query, page: pageParam, perPage }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      getNextVaspPageParam(lastPage, allPages, perPage),
    enabled: enabled && !!accountId && query.length >= MIN_VASP_QUERY_LENGTH,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 10,
  });
};

export interface UseUpdateTravelRuleInfoArgs {
  accountId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useUpdateTravelRuleInfo = ({
  accountId,
  onSuccess,
  onError,
}: UseUpdateTravelRuleInfoArgs) => {
  const instance = useCryptoWalletAPI();
  return useMutationRequest(
    async ({
      whitelistId,
      travelRuleInfo,
    }: {
      whitelistId: string;
      travelRuleInfo: TravelRuleInfo;
    }) =>
      updateTravelRuleInfo(instance, accountId, whitelistId, travelRuleInfo),
    { onSuccess, onError },
  );
};
